import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, TrendingUp, Moon, Activity, Droplets, BookOpen, ArrowRight } from "lucide-react";
import { getWeeklyReport } from "../lib/api";

const MICROS = [
  { key: "mood_avg",          label: "Mood",    Icon: Sparkles,  fmt: (v) => v ? `${v}/5` : "—" },
  { key: "sleep_avg",         label: "Sleep",   Icon: Moon,      fmt: (v) => v ? `${v}h` : "—" },
  { key: "fitness_sessions",  label: "Move",    Icon: Activity,  fmt: (v) => v ? `${v}×` : "—" },
  { key: "hydration_days_met",label: "Hydrate", Icon: Droplets,  fmt: (v) => v ? `${v}d` : "—" },
  { key: "reflections_count", label: "Notes",   Icon: BookOpen,  fmt: (v) => v ? `${v}×` : "—" },
];

export default function WeeklyReportCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getWeeklyReport().then((d) => mounted && setData(d)).catch(() => {}).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="surface-card border border-border rounded-[24px] p-5" data-testid="weekly-report-loading">
        <div className="shimmer h-5 w-2/3 rounded-full" />
        <div className="shimmer h-3 w-full rounded-full mt-3" />
        <div className="shimmer h-3 w-5/6 rounded-full mt-2" />
      </div>
    );
  }

  if (!data) return null;
  const m = data.metrics || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-[24px] bg-secondary text-secondary-foreground p-5"
      data-testid="weekly-report-card"
    >
      <div className="absolute -top-20 -right-20 h-48 w-48 rounded-full bg-primary/25 blur-3xl" />
      <div className="relative">
        <p className="text-[10px] uppercase tracking-wider font-extrabold opacity-80 flex items-center gap-1.5">
          <TrendingUp size={11} /> This week in Knya
        </p>
        <h3 className="text-[22px] leading-tight mt-2 font-extrabold tracking-tight" data-testid="weekly-report-summary">
          {data.summary}
        </h3>

        {/* Metric chips */}
        <div className="mt-4 grid grid-cols-5 gap-1.5">
          {MICROS.map((it) => {
            const v = m[it.key];
            const Icon = it.Icon;
            return (
              <div key={it.key} className="bg-white/8 backdrop-blur rounded-xl p-2 text-center" data-testid={`weekly-metric-${it.key}`}>
                <Icon size={13} className="mx-auto opacity-80" />
                <p className="text-[10px] uppercase tracking-wider font-extrabold opacity-70 mt-0.5">{it.label}</p>
                <p className="text-[13px] font-extrabold tracking-tight mt-0.5">{it.fmt(v)}</p>
              </div>
            );
          })}
        </div>

        {data.current_phase && (
          <p className="mt-3 text-[11.5px] opacity-75 font-medium">
            Currently: <span className="font-extrabold">{data.current_phase}</span>{data.day_of_cycle ? ` · Day ${data.day_of_cycle}` : ""}
          </p>
        )}
      </div>
    </motion.div>
  );
}
